import { ApiError } from '@/api/client/http';
import { isForbidden } from '@/modules/ai/shared/forbidden';

/**
 * What an assistance refusal SAYS.
 *
 * ---------------------------------------------------------------------------
 * THE CODE IS THE FACT; THE SENTENCE IS THE EXPLANATION
 * ---------------------------------------------------------------------------
 * The API's assistance routes answer a refusal with its own code
 * (`ai.assistance.disabled`, `ai.assistance.limit_reached`, …). The code is
 * bound here to ONE plain sentence, so the console says what is true rather
 * than printing a code at a merchant.
 *
 * A code that is not bound here is not guessed at: the server's own message is
 * used, because the server said it and the console has no better answer.
 */
const REFUSAL_WORDS: Readonly<Record<string, string>> = {
  'ai.assistance.disabled': 'AI is turned off for this workspace.',
  'ai.assistance.limit_reached': 'The usage allowance for this period is used up.',
  'ai.assistance.unavailable': 'AI is not available for this workspace right now.',
  'ai.assistance.no_credential': 'This workspace has no AI credential configured.',
  'ai.assistance.no_keyring': 'This workspace has no AI keyring configured.',
  'ai.assistance.provider_unavailable': 'The AI provider could not be reached.',
  'ai.assistance.invalid_output':
    'The answer did not match the contract, so nothing was proposed.',
  'ai.assistance.invalid_request': 'The request was not accepted.',
  'ai.assistance.internal_error': 'The AI assistance request failed.',
};

/**
 * One sentence about why an assistance call did not produce a proposal.
 *
 * A refusal is NOT an empty catalogue and NOT an empty proposal: the merchant
 * is told what stopped it, and the manual form still works.
 */
export function describeAssistanceFailure(error: Error): string {
  if (isForbidden(error)) return 'Not permitted for your role.';
  if (error instanceof ApiError) {
    const words = REFUSAL_WORDS[error.code];
    if (words !== undefined) return words;
    if (error.status === 409) return 'Not right now — the assistant is not available at the moment.';
    if (error.status === 502) return 'The AI provider could not be reached.';
    return error.message;
  }
  return error.message;
}
